<template>
  <div>
    <h1>Профиль</h1>

    <!-- Account Link Warning for non-registered users -->
    <div
      v-if="!$globalState.isRegistered && !warningDismissed"
      class="alert alert-warning alert-dismissible fade show mt-3 mb-3"
      role="alert"
    >
      <h5>Не теряйте свои рецепты!</h5>
      <p>
        Сейчас вы используете анонимный аккаунт. Чтобы сохранять рецепты,
        создавать свою страницу автора, сохранять лайки и историю просмотров и
        иметь доступ к вашему профилю с любого устройства, привяжите ваш аккаунт
        к Google, Яндекс или Telegram.
      </p>
      <p class="mb-0">
        Сделать это можно в меню управления аккаунтом (рядом с кнопкой "Выйти")
      </p>
      <button
        type="button"
        class="btn-close"
        @click="dismissLinkWarning"
        aria-label="Close"
      ></button>
    </div>

    <!-- Author Info Block for registered users -->
    <div v-if="$globalState.isRegistered">
      <!-- Display Mode -->
      <div v-if="!isEditing && originalUserInfo.user_name">
        <div
          @click="startEditing"
          class="author-display-block"
          title="Редактировать"
        >
          <div class="mt-3 d-flex justify-content-between align-items-baseline">
            <p class="mb-0 ms-1 dropdown-toggle h5">
              {{ originalUserInfo.user_name }}
            </p>
            <div v-if="subscriptionStatus" class="small text-muted">
              <span v-if="subscriptionStatus.level > 0" class="text-end">
                {{ subscriptionLevelName }} до {{ formattedExpiresAt }}
              </span>
              <span v-else>{{ subscriptionLevelName }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- User Info / Author Name Block -->
      <div v-else class="card mt-3 mb-3">
        <div class="card-body">
          <!-- Edit Mode -->
          <div v-if="isEditing">
            <h5 class="card-title">Информация об авторе</h5>
            <p class="card-text small">
              Эта информация будет отображаться на вашей публичной странице.
            </p>
            <!-- User Name -->
            <div class="mb-3">
              <label for="authorName" class="form-label"
                >Имя автора (обязательно, уникальное)</label
              >
              <input
                id="authorName"
                type="text"
                class="form-control"
                placeholder="Введите ваше имя автора"
                v-model="userInfo.user_name"
                @keyup.enter="saveUserInfo"
                maxlength="50"
              />
            </div>
            <!-- Description -->
            <div class="mb-3">
              <label for="authorDescription" class="form-label">Описание</label>
              <textarea
                id="authorDescription"
                class="form-control"
                rows="5"
                placeholder="Кратко расскажите о себе"
                v-model="userInfo.description"
                maxlength="500"
              ></textarea>
            </div>
            <!-- Resource -->
            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="resourceName" class="form-label"
                  >Название ресурса</label
                >
                <input
                  id="resourceName"
                  type="text"
                  class="form-control"
                  placeholder="Мой блог"
                  v-model="userInfo.resource_name"
                  maxlength="100"
                />
              </div>
              <div class="col-md-6 mb-3">
                <label for="resourceURL" class="form-label">URL ресурса</label>
                <input
                  id="resourceURL"
                  type="url"
                  class="form-control"
                  placeholder="https://example.com"
                  v-model="userInfo.resource_url"
                  maxlength="2048"
                />
              </div>
            </div>
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <button
                  class="btn btn-primary"
                  type="button"
                  @click="saveUserInfo"
                >
                  Сохранить
                </button>
                <button
                  v-if="originalUserInfo.user_name"
                  class="btn btn-link text-secondary ms-2"
                  @click="cancelEditing"
                >
                  Отмена
                </button>
              </div>
              <a href="#/subscription" class="btn btn-success"
                >Улучшить подписку</a
              >
            </div>
          </div>

          <!-- Status Message -->
          <div
            v-if="userInfoMessage"
            class="form-text mt-2"
            :class="{ 'text-danger': isError, 'text-success': !isError }"
          >
            {{ userInfoMessage }}
          </div>
        </div>
      </div>
    </div>

    <!-- Tabs for switching modes -->
    <div
      class="btn-group w-100 mt-3 mb-3"
      role="group"
      aria-label="Profile sections"
    >
      <!-- My Recipes Tab -->
      <button
        type="button"
        class="btn"
        :class="{
          'btn-secondary': activeTab === 'my-recipes',
          'btn-outline-secondary': activeTab !== 'my-recipes',
        }"
        @click="setActiveTab('my-recipes')"
      >
        Мои
      </button>

      <!-- Likes Tab -->
      <button
        type="button"
        class="btn"
        :class="{
          'btn-secondary': activeTab === 'likes',
          'btn-outline-secondary': activeTab !== 'likes',
        }"
        @click="setActiveTab('likes')"
      >
        Лайки
      </button>

      <!-- Cooked History Tab -->
      <button
        type="button"
        class="btn"
        :class="{
          'btn-secondary': activeTab === 'cooked',
          'btn-outline-secondary': activeTab !== 'cooked',
        }"
        @click="setActiveTab('cooked')"
      >
        Готовил
      </button>

      <!-- Interest Tab -->
      <button
        type="button"
        class="btn"
        :class="{
          'btn-secondary': activeTab === 'interested',
          'btn-outline-secondary': activeTab !== 'interested',
        }"
        @click="setActiveTab('interested')"
      >
        Интересно
      </button>

      <!-- Add more buttons for other sections later -->
    </div>

    <!-- Conditionally render RecipeList based on activeTab -->
    <div>
      <RecipeList v-if="activeTab === 'my-recipes'" mode="my-recipes" />
      <RecipeList v-if="activeTab === 'likes'" mode="likes" />
      <RecipeList v-if="activeTab === 'cooked'" mode="cooked" />
      <RecipeList v-if="activeTab === 'interested'" mode="interested" />
      <!-- Add more RecipeList instances for other modes -->
    </div>

    <!-- Donation Block -->
    <div class="card mt-4 mb-3">
      <div class="card-body text-center">
        <h5 class="card-title">Поддержите Cooksy</h5>
        <p class="card-text small">
          Cooksy развивается и становится лучше благодаря вашей поддержке! Если
          вам нравится наше приложение, вы можете поддержать нас донатом.
          Спасибо!
        </p>
        <a
          href="https://pay.cloudtips.ru/p/13080794"
          class="btn btn-success"
          target="_blank"
          rel="noopener noreferrer"
          >Поддержать проект</a
        >
      </div>
    </div>
  </div>
</template>

<script>
import RecipeList from "../RecipeList.vue";
import { logEvent } from "@/services/analytics.js";

export default {
  name: "PageProfile",
  components: {
    RecipeList, // Register RecipeList
  },
  props: {
    signedIn: Boolean,
  },

  data() {
    return {
      // Load active tab from localStorage or default to 'my-recipes'
      activeTab: localStorage.getItem("profileActiveTab") || "my-recipes",
      userInfo: {
        user_name: "",
        description: "",
        resource_name: "",
        resource_url: "",
      },
      originalUserInfo: {}, // To store the info on load for cancellation
      isEditing: false,
      userInfoMessage: "",
      isError: false,
      warningDismissed: false,
      subscriptionStatus: null,
      subscriptionLoading: true,
    };
  },

  mounted() {
    this.whenLogin().then(() => {
      // Show the sign out button when the page is mounted
      this.$emit("showButton", true);
      // Log initial tab view for analytics
      if (this.$globalState.analytics) {
        logEvent(this.$globalState.analytics, "view_item_list", {
          item_list_id: this.activeTab,
          item_list_name: this.activeTab,
        });
        console.log(
          `Analytics: Logged view_item_list for tab: ${this.activeTab}`
        );
      }
      // Check if the link account warning has been dismissed
      this.warningDismissed = localStorage.getItem("cooksy_hide_link_warning");

      if (this.$globalState.isRegistered) {
        this.loadUserInfo();
        this.loadSubscriptionStatus();
      }
    });
  },

  beforeUnmount() {
    this.$emit("showButton", false);
  },

  computed: {
    subscriptionLevelName() {
      if (!this.subscriptionStatus) return "...";
      switch (this.subscriptionStatus.level) {
        case 1:
          return "Стандарт";
        case 2:
          return "Премиум";
        default:
          return "Бесплатный";
      }
    },
    formattedExpiresAt() {
      if (!this.subscriptionStatus || !this.subscriptionStatus.expires_at)
        return "";
      return new Date(this.subscriptionStatus.expires_at).toLocaleDateString(
        "ru-RU",
        { year: "numeric", month: "long", day: "numeric" }
      );
    },
  },

  methods: {
    async loadUserInfo() {
      try {
        const response = await this.sendCmd("userinfo.get");
        const userInfo = JSON.parse(response);
        this.userInfo = { ...this.userInfo, ...userInfo };
        this.originalUserInfo = { ...userInfo };

        if (!userInfo.user_name) {
          // Show form by default if no name is set
          this.isEditing = true;
        }
      } catch (error) {
        console.error("Failed to load user info:", error);
        this.userInfoMessage = "Не удалось загрузить информацию о профиле.";
        this.isError = true;
        this.isEditing = true; // Show the form on error
      }
    },

    async saveUserInfo() {
      this.userInfoMessage = "";
      this.isError = false;
      if (!this.userInfo.user_name || !this.userInfo.user_name.trim()) {
        this.userInfoMessage = "Имя не может быть пустым.";
        this.isError = true;
        return;
      }
      try {
        const payload = {
          user_name: this.userInfo.user_name.trim(),
          description: this.userInfo.description.trim(),
          resource_name: this.userInfo.resource_name.trim(),
          resource_url: this.userInfo.resource_url.trim(),
        };
        await this.sendCmd("userinfo.save", JSON.stringify(payload));
        console.log("User info saved:");
        this.userInfoMessage = "Информация об авторе успешно сохранена!";
        this.originalUserInfo = { ...this.userInfo };
        this.isError = false;
        this.isEditing = false; // Switch back to display mode
      } catch (error) {
        console.error("Failed to save user info:", error);
        this.userInfoMessage =
          error.message || "Произошла ошибка при сохранении.";
        this.isError = true;
      }
    },

    startEditing() {
      this.isEditing = true;
      this.userInfoMessage = ""; // Clear any previous messages
    },

    cancelEditing() {
      this.userInfo = { ...this.originalUserInfo }; // Revert changes
      this.isEditing = false;
      this.userInfoMessage = "";
    },

    async loadSubscriptionStatus() {
      this.subscriptionLoading = true;
      try {
        const response = await this.sendCmd("subscription.getStatus");
        this.subscriptionStatus = JSON.parse(response);
      } catch (error) {
        console.error("Failed to load subscription status:", error);
      } finally {
        this.subscriptionLoading = false;
      }
    },

    dismissLinkWarning() {
      this.warningDismissed = true;
      // Store the user's preference in local storage.
      localStorage.setItem("cooksy_hide_link_warning", "true");
    },

    setActiveTab(tabName) {
      this.activeTab = tabName;
      // Save the active tab to localStorage
      localStorage.setItem("profileActiveTab", tabName);
      // Log tab switch for analytics
      if (this.$globalState.analytics) {
        logEvent(this.$globalState.analytics, "view_item_list", {
          item_list_id: tabName,
          item_list_name: tabName,
        });
        console.log(`Analytics: Logged view_item_list for tab: ${tabName}`);
      }
    },
    /**
     * @description Emits a "needLogout" event with a value of true,
     * to let the parent component know that the user needs to log out.
     * After a short delay, emits another "needLogout" event with a value of false.
     * @emits needLogout
     * @listens needLogout
     */
    doNeedLogout() {
      this.$emit("needLogout", false);
      setTimeout(() => {
        this.$emit("needLogout", true);
      }, 100);

      /**
       * After a short delay, emit another "needLogout" event with a value of false,
       * to let the parent component know that the user no longer needs to log out.
       */
      setTimeout(() => {
        this.$emit("needLogout", false);
        // window.location.href = "/";
      }, 100);
    },
  },
};
</script>

<style scoped>
.signout-button {
  position: absolute;
  top: 73px;
  right: 12px;
}
.author-display-block {
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  border-radius: var(--bs-border-radius);
}
.author-display-block:hover {
  background-color: var(--bs-tertiary-bg);
}
</style>
